import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import logo from "@/assets/bimbee-logo.webp";

const apps = [
  { name: "RevKit", path: "/revkit" },
  { name: "Listools", path: "/listools" },
  { name: "RapidRebar", path: "/rapidrebar" },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className={`sticky top-0 z-50 border-b border-border/50 backdrop-blur-xl transition-all duration-300 ${isScrolled ? "bg-card/80 shadow-md" : "bg-card/30"}`}>
      <div className="container flex h-16 items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          <span className="text-xl font-bold text-foreground">Bimbee</span>
          <img src={logo} alt="Bimbee" className="h-14 w-14" />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden items-center gap-8 md:flex">
          <Link
            to="/"
            className={`text-base font-medium transition-colors hover:text-primary ${isActive("/") ? "text-primary" : "text-muted-foreground"}`}
          >
            דף הבית
          </Link>

          {/* Dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setDropdownOpen(true)}
            onMouseLeave={() => setDropdownOpen(false)}
          >
            <button
              onClick={() => setDropdownOpen((prev) => !prev)}
              className={`flex items-center gap-1 text-base font-medium transition-colors hover:text-primary ${apps.some((a) => isActive(a.path)) ? "text-primary" : "text-muted-foreground"}`}
            >
              אפליקציות
              <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`} />
            </button>
            {dropdownOpen && (
              <div className="absolute top-full right-0 pt-1 w-44">
                <div className="rounded-lg border border-border bg-card p-1.5 shadow-elevated animate-fade-in">
                  {apps.map((app) => (
                    <Link
                      key={app.path}
                      to={app.path}
                      onClick={() => setDropdownOpen(false)}
                      className={`block rounded-md px-3 py-2 text-base transition-colors hover:bg-accent ${isActive(app.path) ? "bg-accent text-primary font-medium" : "text-foreground"}`}
                    >
                      {app.name}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Link
            to="/about"
            className={`text-base font-medium transition-colors hover:text-primary ${isActive("/about") ? "text-primary" : "text-muted-foreground"}`}
          >
            אודות
          </Link>
        </nav>

        <div className="flex items-center gap-3">
          <Button asChild className="hidden gradient-cta text-foreground shadow-card hover:opacity-90 hover-scale pulse md:inline-flex">
            <Link to="/#contact-form?intent=trial" onClick={() => setTimeout(() => document.getElementById("contact-form")?.scrollIntoView({ behavior: "smooth" }), 150)}>התחילו ניסיון חינם</Link>
          </Button>

          <button
            className="md:hidden text-foreground"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="תפריט"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="border-t border-border bg-card md:hidden">
          <nav className="container flex flex-col gap-1 py-4">
            <Link to="/" onClick={() => setMobileOpen(false)} className="rounded-md px-3 py-2.5 text-base font-medium hover:bg-accent">דף הבית</Link>
            <p className="px-3 pt-3 pb-1 text-sm font-semibold text-muted-foreground">אפליקציות</p>
            {apps.map((app) => (
              <Link key={app.path} to={app.path} onClick={() => setMobileOpen(false)} className="rounded-md px-5 py-2 text-base hover:bg-accent">{app.name}</Link>
            ))}
            <Link to="/about" onClick={() => setMobileOpen(false)} className="rounded-md px-3 py-2.5 text-base font-medium hover:bg-accent">אודות</Link>
            <Button asChild className="mt-3 gradient-cta text-foreground">
              <Link to="/#contact-form?intent=trial" onClick={() => { setMobileOpen(false); setTimeout(() => document.getElementById("contact-form")?.scrollIntoView({ behavior: "smooth" }), 150); }}>התחילו ניסיון חינם</Link>
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
}
